import {
  AlignItems,
  Body1,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Heading1,
  JustifyContent,
  Space,
} from '@gousto-internal/citrus-react'
import { getEventNamesFromUUID } from '@library/api-motivations'
import { Motivations } from '@library/api-motivations/src/types'
import Immutable from 'immutable'
import React, { useState } from 'react'
import { SignupMotivationsSelector } from './components/SignupMotivationsSelector/SignupMotivationsSelector'
import { CTA_TEXT, SIGNUP_MOTIVATIONS_TEXT } from './config'
import {
  trackSkipClick,
  trackContinueClick,
  deselectMotivations,
  selectMotivations,
} from './tracking'

type SignupMotivationsProps = {
  defaultSignupMotivations?: Immutable.List<string>
  onContinue: (signupMotivations: Immutable.List<string>) => void
  onSkip: (signupMotivations: Immutable.List<string>) => void
  motivations: Motivations[]
}

export function SignupMotivations({
  onContinue,
  onSkip,
  defaultSignupMotivations = Immutable.List(),
  motivations,
}: SignupMotivationsProps) {
  const [hasSignupMotivations, setHasSignupMotivations] = useState<boolean>(
    defaultSignupMotivations.size > 0,
  )
  const [signupMotivations, setSignupMotivations] =
    useState<Immutable.List<string>>(defaultSignupMotivations)

  const isContinueDisabled = signupMotivations.size < 1

  const handleHasSignupMotivations = (hasSignupMotivation: boolean) => {
    setHasSignupMotivations(hasSignupMotivation)
    if (!hasSignupMotivation) {
      setSignupMotivations(Immutable.List())
    }
  }

  const handleSignupMotivationsChange = (pref: string): void => {
    const isSelected = signupMotivations.includes(pref)
    const updatedSignupMotivations = isSelected
      ? (signupMotivations.filter((item) => item !== pref) as Immutable.List<string>)
      : signupMotivations.push(pref)
    setSignupMotivations(updatedSignupMotivations)
    setHasSignupMotivations(true)

    const eventNames = getEventNamesFromUUID([pref])
    if (isSelected) {
      deselectMotivations(eventNames)
    } else {
      selectMotivations(eventNames)
    }
  }

  const handleSkipCTAClick = () => {
    trackSkipClick()
    onSkip(defaultSignupMotivations)
  }

  const handleContinueCTAClick = () => {
    if (!isContinueDisabled) {
      trackContinueClick(getEventNamesFromUUID(signupMotivations.toJS()))
      onContinue(signupMotivations)
    }
  }

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      justifyContent={JustifyContent.Center}
      alignItems={AlignItems.Center}
    >
      <Box width="100%">
        <Heading1 size={[9, 10]}>{SIGNUP_MOTIVATIONS_TEXT.title}</Heading1>
        <Space size={[2, 4]} />
        <Body1 color={Color.ColdGrey_800} size={[2, 4]}>
          {SIGNUP_MOTIVATIONS_TEXT.description}
        </Body1>
        <Space size={3} />
      </Box>
      <Space size={[4, 6]} />
      <Box width={['100%', '392px']}>
        <SignupMotivationsSelector
          hasSignupMotivations={hasSignupMotivations}
          onHasSignupMotivationsChange={handleHasSignupMotivations}
          signupMotivations={signupMotivations}
          onSignupMotivationsChange={handleSignupMotivationsChange}
          motivations={motivations}
        />
      </Box>
      <Space size={[4, 6]} />
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
        width={'100%'}
        gap={2}
      >
        <Button
          height="48px"
          width={['100%', '392px']}
          data-testid="signup-motivations-continue-cta"
          onClick={handleContinueCTAClick}
          disabled={isContinueDisabled}
          aria-disabled={isContinueDisabled}
          aria-label={CTA_TEXT.CONTINUE}
        >
          {CTA_TEXT.CONTINUE}
        </Button>
        <Button
          height="48px"
          width={['100%', '392px']}
          data-testid="signup-motivations-skip-cta"
          onClick={handleSkipCTAClick}
          colorVariant={ButtonColorVariant.Tertiary}
          aria-label={CTA_TEXT.SKIP}
        >
          {CTA_TEXT.SKIP}
        </Button>
      </Box>
    </Box>
  )
}
